import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, fetchUserDetail, clearSelectedUser } from '../../redux/slices/usersSlice';
import TableHelper from '../Shared/TableHelper';
import './UsersList.css';

const UsersList = () => {
  const dispatch = useDispatch();
  const { items, selectedUser, error } = useSelector((state) => state.users);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    dispatch(fetchUsers({ q: search, filter }));
  }, [search, filter, dispatch]);

  const handleSelectUser = (userId) => {
    dispatch(fetchUserDetail({ userId }));
  };

  const handleCloseDetail = () => {
    dispatch(clearSelectedUser());
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Usuario',
        accessor: 'name',
        Cell: ({ row: { original: user } }) => (
          <div className="user-cell">
            <div className="avatar">{user.name[0]?.toUpperCase()}</div>
            <div>
              <strong>{user.name}</strong>
              <small style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px' }}>
                {user.email}
              </small>
            </div>
          </div>
        ),
      },
      {
        Header: 'Tipo',
        accessor: 'role',
        Cell: ({ value }) => (
          <span className="badge">{value === 'employer' ? 'Empleador' : 'Profesional'}</span>
        ),
      },
      {
        Header: 'Ciudad',
        accessor: 'city',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: 'Actividad',
        accessor: 'jobs_count',
        Cell: ({ row: { original: user } }) => (
          <span>
            {user.role === 'employer'
              ? `${user.jobs_count} ofertas`
              : `${user.applications_count} candidaturas`}
          </span>
        ),
      },
      {
        Header: 'Alta',
        accessor: 'created_at',
        Cell: ({ value }) => new Date(value).toLocaleDateString('es-ES'),
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row: { original: user } }) => (
          <button
            onClick={() => handleSelectUser(user.id)}
            className="btn btn-sm btn-link"
          >
            Detalles
          </button>
        ),
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="users-container">
      <div className="users-header">
        <h1>Usuarios</h1>
        <p>Profesionales y empresas registradas.</p>
      </div>

      <div className="users-toolbar">
        <input
          type="text"
          placeholder="Filtrar por nombre, correo o ciudad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="">Todos los perfiles</option>
          <option value="candidate">Profesionales</option>
          <option value="employer">Empleadores</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className={`users-layout ${selectedUser ? 'has-detail' : ''}`}>
        <div className="users-table-container">
          <TableHelper columns={columns} data={items} exportFileName="usuarios" />
        </div>

        {selectedUser && (
          <aside className="detail-panel">
            <button onClick={handleCloseDetail} className="btn-close">×</button>
            <div className="detail-avatar">{selectedUser.name[0]?.toUpperCase()}</div>
            <span className="detail-type">
              {selectedUser.role === 'employer' ? 'EMPLEADOR' : 'PROFESIONAL'}
            </span>
            <h2>{selectedUser.name}</h2>

            <dl className="detail-info">
              <dt>Correo</dt>
              <dd>{selectedUser.email}</dd>
              <dt>Teléfono</dt>
              <dd>{selectedUser.phone || 'No indicado'}</dd>
              <dt>Ciudad</dt>
              <dd>{selectedUser.city || 'No indicada'}</dd>
              <dt>Alta</dt>
              <dd>{new Date(selectedUser.created_at).toLocaleString('es-ES')}</dd>
            </dl>

            <h3>Presentación</h3>
            <p className="bio">{selectedUser.bio || 'Sin descripción.'}</p>

            {selectedUser.role === 'employer' ? (
              <>
                <h3>Ofertas publicadas ({selectedUser.jobs_count ?? 0})</h3>
                {selectedUser.jobs && selectedUser.jobs.length > 0 ? (
                  <ul className="detail-activity-list">
                    {selectedUser.jobs.map((job) => (
                      <li key={job.id}>
                        <div>
                          <strong>{job.title}</strong>
                          <small>{job.city || 'Sin ciudad'} · {job.applications_count} candidaturas</small>
                        </div>
                        <span className={`badge ${job.active ? 'active' : 'inactive'}`}>
                          {job.active ? 'Activa' : 'Pausada'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="bio">Todavía no ha publicado ninguna oferta.</p>
                )}
              </>
            ) : (
              <>
                <h3>Candidaturas ({selectedUser.applications_count ?? 0})</h3>
                {selectedUser.applications && selectedUser.applications.length > 0 ? (
                  <ul className="detail-activity-list">
                    {selectedUser.applications.map((app) => (
                      <li key={app.id}>
                        <div>
                          <strong>{app.job_title}</strong>
                          <small>{app.company || 'Empresa no indicada'}</small>
                        </div>
                        <span className="badge">{app.status}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="bio">Todavía no se ha inscrito en ninguna oferta.</p>
                )}
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
};

export default UsersList;
